import { actions } from '@ecosystem/newron-design-system';
import { actionCreatorsSideFilters } from '@state/Index';
import { getInvalidPGBySelectedFilterKeys, getInvalidPGs } from '@utils/CountryUtils';
import {
  clearAllFilters,
  getBaseFilterKeysByPageTypeAndPageName,
  getFilterSettingsByPage,
  sortFiltersChips,
} from '@utils/FilterSettings';
import { getAndReplacePriceBandIndexToPriceBandRange, isOnlyBaseFiltersApplied } from '@utils/FilterUtils';
import { getCarryOverAllFiltersForDelete, removeFilterByKey } from '@utils/filterTrayUtils/FilterTrayUtils';
import { checkIsMultiCountryClient } from '@utils/multiCountryUtils/MultiCountryUtils';
import { usePageConfig } from '@utils/pageConfig/PagesConfigContext';
import {
  CountryFilterPair,
  FilterKeys,
  Filters,
  getPageTypeWithPageName,
  Option,
  PageName,
  PageType,
  SavedFilters,
} from '@utils/pageConfig/Types';
import React, { Fragment, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { bindActionCreators } from 'redux';
import { checkIsFilterApplied } from '@utils/CheckIsFilterApplied';
import SavedFiltersModal from '../saved-filters-modal/SavedFiltersModal';
import { Periodicity } from '../time-period-selector/PeriodsTypes';
import { byFilterKey } from '@utils/pageConfig/Predicates/SelectedFiltersPredicates';
import { cloneDeep, difference, isEqual } from 'lodash';
import {
  ELEMENT_CATEGORY_BTN,
  PAGE_ELEMENT_CLICK_TRACKING_TITLE,
  pageElementClickTrackingFunction,
  PageElementClickTrackingParam,
  SAVE_FILTER_BTN,
} from '@utils/page-analytics/model/PageElementClickedTracking';
import { IRootState } from '@state/reducers/Reducers';
import { useAnalytics } from '@ecosystem/newron-client-lib';

/* eslint-disable-next-line */
export interface FilterChipsProps {
  pgListByCountry: CountryFilterPair[];
  selectedTimeFrame: Periodicity;
}

const FiltersChipsWrapper = (props: { children: any }) => (
  <div data-test-id="filters-chips-group-wrapper">{props.children}</div>
);

const FilterChips: React.FC<FilterChipsProps> = ({ pgListByCountry, selectedTimeFrame }) => {
  const pagesConfig = usePageConfig();
  const retailerClientState = useSelector((state: IRootState) => state.retailerClient);

  const title = 'Filtered by:';

  const dispatch = useDispatch();
  const { track } = useAnalytics();
  const { updateSideFilters } = bindActionCreators(actionCreatorsSideFilters, dispatch);
  const sideFiltersState = useSelector((state: any) => state.sideFilters);
  const { pageName } = pagesConfig;
  const [isSaveFiltersModalShown, setIsSaveFiltersModalShown] = useState<boolean>(false);

  const handleElementClickTracking = () => {
    const params: PageElementClickTrackingParam = {
      sideFilters: sideFiltersState.appliedFilters,
      eventTitle: PAGE_ELEMENT_CLICK_TRACKING_TITLE,
      retailerClientState: retailerClientState,
      elementCategory: ELEMENT_CATEGORY_BTN,
      elementName: SAVE_FILTER_BTN,
      pageType: getPageTypeWithPageName(pageName),
      pageName: pageName,
      access: pagesConfig.access,
      track: track,
    };
    pageElementClickTrackingFunction(params);
  };
  const displaySaveFiltersModal = () => {
    setIsSaveFiltersModalShown(true);
    handleElementClickTracking();
  };

  const hideSaveFiltersModal = () => {
    setIsSaveFiltersModalShown(false);
  };

  /**
   * function that deletes the particular filter from the applied filters
   * also checks if only base filters are left in pg overview or not. if yes, clear all button will be disabled
   */
  const deleteFilter = (filterKey: FilterKeys, toRemove: string, filterTitle: string) => {
    const carriedOverAllFilters = getCarryOverAllFiltersForDelete(
      pagesConfig.appliedFilters,
      pagesConfig.getPageTypeAndName(),
      filterKey,
      toRemove,
      filterTitle,
    );

    pagesConfig.setSelectedFilters(carriedOverAllFilters);
    pagesConfig.setAppliedFilters(carriedOverAllFilters);
    pagesConfig.setClearFilters(true);

    const storeFilterSettings = {
      appliedFilters: carriedOverAllFilters,
    };
    updateSideFilters(storeFilterSettings);

    if (isOnlyBaseFiltersApplied(pagesConfig.getPageTypeAndName(), carriedOverAllFilters)) {
      pagesConfig.setClearFilters(false);

      const storeUpdatedFilterSettings = {
        appliedFilters: carriedOverAllFilters,
      };
      updateSideFilters(storeUpdatedFilterSettings);
    }
  };

  /*
   * function that handles the clearing of all applied filters
   */
  const onClear = () => {
    const carriedOverAllFilters = getCarryOverAllFiltersForDelete(
      pagesConfig.appliedFilters,
      pagesConfig.getPageTypeAndName(),
    );

    const allFilters = clearAllFilters(
      carriedOverAllFilters,
      pagesConfig.getPageTypeAndName(),
      pagesConfig.isMultiCountryDropdown,
    );

    if (pagesConfig.pageType === PageType.EXPLORE) {
      const isSingleCountry =
        allFilters[PageType.EXPLORE].selectedFilters.filter(byFilterKey(FilterKeys.COUNTRY)).flatMap((c) => c.options)
          .length === 1;

      if (isSingleCountry) {
        removeFilterByKey(allFilters[PageType.EXPLORE].selectedFilters, FilterKeys.PRODUCT_GROUP);
      }
    }
    pagesConfig.setSelectedFilters(allFilters);
    pagesConfig.setAppliedFilters(allFilters);
    pagesConfig.setClearFilters(false);

    const storeFilterSettings = {
      appliedFilters: allFilters,
    };

    updateSideFilters(storeFilterSettings);
  };

  const renderClearAllButton = (filters: Filters[], countries: Option[]) => {
    const isMultipleCountries = countries.length > 1;
    const isMultiplePg: boolean =
      filters.filter((f) => f.filterKey === FilterKeys.PRODUCT_GROUP).flatMap((f) => f.options).length > 1;

    if (
      isOnlyBaseFiltersApplied(pagesConfig.getPageTypeAndName(), pagesConfig.appliedFilters, isMultipleCountries) &&
      !isMultipleCountries &&
      !isMultiplePg
    ) {
      return null;
    }

    //to hide the clear all button
    //run checking with base filter for PA page without multi country flag
    //due to passing the multi country flag when getting base filter will return base filter without country and cause the
    //clear all button to be render when base filter etc country, pg and currency left in PA page
    if (pagesConfig.pageType === PageType.PERFORMANCE_ANALYSIS) {
      if (isOnlyBaseFiltersApplied(pagesConfig.getPageTypeAndName(), pagesConfig.appliedFilters)) {
        return null;
      }
    }

    if (pagesConfig.pageType === PageType.EXPLORE || pagesConfig.pageType === PageType.OPPORTUNITY_REVIEW || pagesConfig.pageType === PageType.TRADING_ASSISTANT) {
      const isSingleCountry =
        pagesConfig.appliedFilters[PageType.EXPLORE].selectedFilters
          .filter(byFilterKey(FilterKeys.COUNTRY))
          .flatMap((c) => c.options).length === 1;
      const noPGSelected =
        pagesConfig.appliedFilters[PageType.EXPLORE].selectedFilters
          .filter(byFilterKey(FilterKeys.PRODUCT_GROUP))
          .flatMap((c) => c.options).length === 0;

      const sidefilterKeys = Object.values(FilterKeys)
        .filter((value) => typeof value === 'number')
        .slice(2); //other than country and pg

      const isOtherSideFiltersApplied = sidefilterKeys.some(
        (filterKey) =>
          !!pagesConfig.appliedFilters[PageType.EXPLORE].selectedFilters
            .filter(byFilterKey(filterKey))
            .flatMap((c) => c.options).length,
      );

      if (isSingleCountry && noPGSelected && !isOtherSideFiltersApplied) {
        return null;
      }
    }

    const { Chips } = actions;
    return (
      <div className="ml-4">
        <Chips.Actions.ClearAll onClick={onClear} />
      </div>
    );
  };

  const renderSaveButton = (filters: Filters[]) => {
    if (filters.length === 0) {
      return null;
    }
    const { Button } = actions;
    const { BookmarkOutline } = Button.Icon.icons;
    return (
      <Button mode="simple" testId={'save-filter-button'} onClick={displaySaveFiltersModal}>
        <Button.Icon>
          <BookmarkOutline />
        </Button.Icon>
        Save filter
      </Button>
    );
  };

  const isBaseFilter = (filter: Filters) => {
    let isMultipleCountries = false;
    //Check for length of country filter chip to determine rendering of delete button
    if (filter.filterKey === FilterKeys.COUNTRY) {
      isMultipleCountries = filter.options.length > 1;

      if (pagesConfig.pageType === PageType.EXPLORE) {
        return !isMultipleCountries;
      }
    }
    const isMultiCountryClient = checkIsMultiCountryClient(pagesConfig.clientId);
    const baseFilterKeys = getBaseFilterKeysByPageTypeAndPageName(
      pagesConfig.getPageTypeAndName(),
      [filter], // Wrap into an array for fulfill the method
      isMultipleCountries && isMultiCountryClient,
    );

    return baseFilterKeys.includes(filter.filterKey);
  };

  const isExploreBaseFilter = (filter: Filters) => {
    let isMultipleCountries =
      pagesConfig.appliedFilters[PageType.EXPLORE].selectedFilters
        .filter(byFilterKey(FilterKeys.COUNTRY))
        .flatMap((country) => country.options).length > 1;
    let isMultiplePgs =
      pagesConfig.appliedFilters[PageType.EXPLORE].selectedFilters
        .filter(byFilterKey(FilterKeys.PRODUCT_GROUP))
        .flatMap((pg) => pg.options).length > 1;
    //Check for length of country filter chip to determine rendering of delete button
    if (filter.filterKey === FilterKeys.COUNTRY) {
      return !isMultipleCountries;
    }
    if (filter.filterKey === FilterKeys.PRODUCT_GROUP) {
      return isMultipleCountries && !isMultiplePgs;
    }
    const isMultiCountryClient = checkIsMultiCountryClient(pagesConfig.clientId);
    const baseFilterKeys = getBaseFilterKeysByPageTypeAndPageName(
      pagesConfig.getPageTypeAndName(),
      [filter], // Wrap into an array for fulfill the method
      isMultipleCountries && isMultiCountryClient,
    );

    return baseFilterKeys.includes(filter.filterKey);
  };

  const renderFilterChip = (filter: Filters, countries: Option[]) => {
    const isHideButtonClick =
      pagesConfig.pageType === PageType.EXPLORE ? isExploreBaseFilter(filter) : isBaseFilter(filter);

    const isBrandFilter = filter.filterKey === FilterKeys.BRAND;
    const isExclusionEnabled = filter.isExclusion === true;

    return filter.options.map((option) => {
      const buttonClick = isHideButtonClick
        ? undefined
        : () => {
            deleteFilter(filter.filterKey, option.value, filter.filterTitle);
          };

      const filterKeys: FilterKeys[] = [
        PageName.RETAILER_OVERVIEW,
        PageName.CATEGORY_LEADERBOARD,
        PageName.EXPLORE,
      ].includes(pageName)
        ? [FilterKeys.ONLINE_OFFLINE, FilterKeys.CITY, FilterKeys.REGION, FilterKeys.BRAND]
        : [];

      const isPGInvalid = () => {
        const isPGFilter = filter.filterKey === FilterKeys.PRODUCT_GROUP;

        const isInvalidPg = getInvalidPGs(selectedTimeFrame, [option], countries, pgListByCountry).length !== 0;
        const hasInvalidPgFeatureValues =
          getInvalidPGBySelectedFilterKeys(
            option,
            pagesConfig.productGroupFeatureValueList,
            sideFiltersState.appliedFilters[pagesConfig.pageType].selectedFilters,
            filterKeys,
          ).length > 0;
        return isPGFilter && (isInvalidPg || hasInvalidPgFeatureValues);
      };

      const isPGNotSubscribed = () =>
        getInvalidPGs(selectedTimeFrame, [option], countries, pgListByCountry).length !== 0;
      const isCurrencyFilter = filter.filterKey === FilterKeys.CURRENCY;
      const hasCurrencyFilter =
        isCurrencyFilter &&
        sideFiltersState.appliedFilters[pagesConfig.pageType].selectedFilters.some(byFilterKey(FilterKeys.CURRENCY));

      const shouldRenderFilterChip = !(
        (pageName === PageName.TRADING_ASSISTANT && isBrandFilter) ||
        (isCurrencyFilter && !hasCurrencyFilter) ||
        (FilterKeys.PRICEBAND === filter.filterKey && countries.length > 1)
      );

      const { Chips } = actions;
      return shouldRenderFilterChip ? (
        <Chips.Chip
          testId={option.label}
          key={option.label}
          value={option.label}
          label={isExclusionEnabled ? `${filter.filterTitle} (Excl.)` : `${filter.filterTitle}`}
          onRemove={buttonClick}
          error={isPGNotSubscribed() && isPGInvalid()}
        />
      ) : null;
    });
  };

  const getFilterByFilterKeyAllowed = (filters: Filters[]): Filters[] => {
    let filterKeyAllow = getFilterSettingsByPage(PageType.EXPLORE).map((fs) => fs.filterKey);
    if (pagesConfig.pageName !== PageName.PERFORMANCE_ANALYSIS && pagesConfig.pageName !== PageName.EXPLORE) {
      filterKeyAllow = filterKeyAllow.filter((key: FilterKeys) => key !== FilterKeys.CURRENCY);
    }
    let saveFilterKey = filters.map((key) => key.filterKey);
    let notAllowFilterKey = difference(saveFilterKey, filterKeyAllow);

    return filters.filter((f) => {
      return !notAllowFilterKey.includes(f.filterKey);
    });
  };

  const getSavedFilters = pagesConfig.savedFilters[pagesConfig.pageType].savedFilters;

  const appliedFilters = cloneDeep(pagesConfig.appliedFilters[pagesConfig.pageType].selectedFilters);
  if (!appliedFilters || appliedFilters.length === 0) {
    return null;
  }
  /*
   * function to render the filter chips
   */
  const toRender = (filters: Filters[]) => {
    // Only used at first load from RO/CL apply market definition side filters and navigate to OR/PA page
    const sortedFilterChips = sortFiltersChips(filters);

    const countries: Option[] = sortedFilterChips
      .filter((f) => f.filterKey === FilterKeys.COUNTRY)
      .flatMap((f) => f.options);

    const getPricebandFilters = (savedFilter: SavedFilters, periodicity: string, savedFilterCountry: string[]) => {
      const sideFilterList = pagesConfig.productGroupPriceBandValueList.find(
        (pgPriceband) =>
          savedFilterCountry.includes(pgPriceband.country) && isEqual(pgPriceband.periodType, periodicity),
      )?.productGroupSideMenuFilterDtoList;

      const pg = savedFilter.filters.find((filter) => filter.filterKey === FilterKeys.PRODUCT_GROUP);
      return getAndReplacePriceBandIndexToPriceBandRange(savedFilter, sideFilterList, pg?.options[0].value!);
    };

    const appliedSavedFilter = getSavedFilters.find((savedFilter) =>
      checkIsFilterApplied(
        sortFiltersChips(appliedFilters),
        sortFiltersChips(
          getFilterByFilterKeyAllowed(
            getPricebandFilters(
              savedFilter,
              selectedTimeFrame,
              countries.map((country) => country.label),
            ).filters,
          ),
        ),
      ),
    );

    const Wrapper = appliedSavedFilter ? FiltersChipsWrapper : Fragment;
    const { Chips } = actions;
    return (
      <div className="border-2 border-light flex rounded-2 px-6 py-4 justify-between items-center w-full">
        <div className="flex-1" data-test-id="applied-filter-chips">
          <Chips>
            <Chips.Label>{title}</Chips.Label>
            <Wrapper>
              <div className={`${sortedFilterChips.length === 1 ? 'retail-single-saved-filter-chip' : ''} flex-1`}>
                <Chips.List>{sortedFilterChips.map((filter) => renderFilterChip(filter, countries))}</Chips.List>
              </div>
            </Wrapper>
          </Chips>
        </div>
        <div className="flex">
          {renderClearAllButton(sortedFilterChips, countries)}
          {renderSaveButton(sortedFilterChips)}
        </div>
      </div>
    );
  };

  const toRenderInModal = (filters: Filters[]) => {
    const { Chips } = actions;
    return (
      <div>
        <Chips>
          <Chips.List>
            {filters.map((filter) => {
              const isExclusionMode = filter.isExclusion === true;

              return filter.options.map((option) => (
                <Chips.Chip
                  key={option.label}
                  value={option.label}
                  label={isExclusionMode ? `${filter.filterTitle} (Excl.)` : `${filter.filterTitle}`}
                />
              ));
            })}
          </Chips.List>
        </Chips>
      </div>
    );
  };

  return (
    <div>
      <div>{toRender(appliedFilters)}</div>
      <div>
        <SavedFiltersModal
          isSaveModalOpen={isSaveFiltersModalShown}
          onClose={hideSaveFiltersModal}
          toRender={toRenderInModal(appliedFilters)}
        />
      </div>
      <br />
    </div>
  );
};

export default FilterChips;
